/* eslint-disable @typescript-eslint/no-explicit-any */
import { SelectInput } from "components/Select";
import { useFilterActions } from "../../ducks/filter/hooks/actions";
import { useFilterState } from "../../ducks/filter/hooks/filterState";
import {
  useGetRegionalResponsibleQuery,
  useGetStoresResponsiblesOptionsQuery,
} from "@/services/store/select-options";
import { RoleEnum } from "@/enum/role";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import { useExportLateReconciliationsExcelMutation } from "@/services/store/store";
import { downloadExcelBlob } from "@/utils/download-excel";
import { format } from "date-fns";
import { toast } from "components/ui/use-toast";

type SelectOption = { value: string; label: string };

export default function SelectFilters() {
  const { updateFilter } = useFilterActions();
  const filterState = useFilterState();
  const { filters } = filterState || { filters: {} };

  const { data: responsibleOptions } = useGetStoresResponsiblesOptionsQuery({
    role: RoleEnum.ANALISTA,
  });
  const { data: regionOptions } = useGetRegionalResponsibleQuery();
  const [exportExcel, { isLoading: isExporting }] = useExportLateReconciliationsExcelMutation();

  const handleSelectResponsibleChange = (options: unknown) => {
    const opts = options as SelectOption[] | null;
    const values = opts?.map((opt) => opt.value) || [];
    updateFilter({ key: "responsible", value: values.length > 0 ? values : undefined });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const handleSelectRegionResponsibleChange = (options: unknown) => {
    const opts = options as SelectOption[] | null;
    const values = opts?.map((opt) => opt.value) || [];
    updateFilter({ key: "regional_id", value: values.length > 0 ? values : undefined });
    updateFilter({ key: "meta", value: { page: 1 } });
  };

  const getSelectedResponsibles = () => {
    const values = filters.responsible;
    if (!values || !Array.isArray(values)) return [];
    return responsibleOptions?.filter((opt: SelectOption) => values.includes(opt.value)) || [];
  };

  const getSelectedRegionals = () => {
    const values = filters.regional_id;
    if (!values || !Array.isArray(values)) return [];
    return regionOptions?.filter((opt: SelectOption) => values.includes(opt.value)) || [];
  };

  const getResponsibleFilter = () => {
    if (!filters.responsible || (Array.isArray(filters.responsible) && filters.responsible.length === 0)) return undefined;
    return filters.responsible;
  };

  const getRegionalFilter = () => {
    if (!filters.regional_id || (Array.isArray(filters.regional_id) && filters.regional_id.length === 0)) return undefined;
    return filters.regional_id;
  };

  const handleExportExcel = async () => {
    try {
      const { from, to } = filters.dateRange ?? {};
      const exportFilters = {
        ...(getResponsibleFilter() !== undefined && { responsible: getResponsibleFilter() }),
        ...(getRegionalFilter() !== undefined && { regional_id: getRegionalFilter() }),
        ...(from !== undefined && {
          transactionStartDate: format(new Date(from), "yyyy-MM-dd") + "T00:00:00.000Z",
        }),
        ...(to !== undefined && {
          transactionEndDate: format(new Date(to), "yyyy-MM-dd") + "T23:59:59.999Z",
        }),
      };

      const result = await exportExcel(exportFilters).unwrap();
      const fileName = `conciliacoes-atrasadas-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      downloadExcelBlob(result, fileName);
    } catch (error) {
      console.error("Erro ao exportar Excel:", error);
      toast({
        description: "Erro ao exportar relatório para Excel.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <SelectInput
          options={responsibleOptions}
          placeholder="Selecionar analistas"
          onChange={handleSelectResponsibleChange}
          value={getSelectedResponsibles()}
          className="min-w-[250px]"
          isClearable
          isMulti
        />
        <SelectInput
          options={regionOptions}
          placeholder="Selecionar regionais"
          onChange={handleSelectRegionResponsibleChange}
          value={getSelectedRegionals()}
          className="min-w-[250px]"
          isClearable
          isMulti
        />
      </div>
      <Button
        className="h-8 bg-[#F6D040] text-[#613F4F] hover:bg-[#F6D040]/90"
        onClick={handleExportExcel}
        disabled={isExporting}
      >
        <FileSpreadsheet className="mr-2 h-4 w-4" />
        {isExporting ? "Exportando..." : "Exportar Excel"}
      </Button>
    </div>
  );
}
